import {toast} from '@wandb/weave/common/components/elements/Toast';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import * as d3 from 'd3';
import produce from 'immer';
import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import ReactDOM from 'react-dom';

import {urlPrefixed} from '../../config';
import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {useSetHighlight} from '../../state/interactState/hooks';
import {RunWithRunsetInfo} from '../../state/runs/types';
import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as GroupSelectionsActionsInternal from '../../state/views/groupSelections/actionsInternal';
import * as ViewHooks from '../../state/views/hooks';
import * as ColorUtil from '../../util/colors';
import {PLASMA_GRADIENT} from '../../util/colors';
import {NULL_STRING_ANGLE_BRACKETS} from '../../util/constants';
import {legendHTMLFromRun} from '../../util/plotHelpers/legend';
import * as Run from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import {RunColorConfig} from '../../util/section';
import * as SM from '../../util/selectionmanager';
import {run as runURL} from '../../util/urls';
import {GradientStop, gradientToRGBAValues} from '../GradientPicker';
import {PCColumn, PCConfig} from '../PanelParallelCoord/common';
import {usePanelGroupingSettings} from '../PanelRunsLinePlot/RunsLinePlotContext/usePanelGroupingSettings';
import {SelectionBoundsMenu} from '../SelectionBoundsMenu';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {useConvertSelectionsToFilters} from './useConvertSelectionsToFilters';
import {Dimension, getAccessorsWithDimensions, rgbaStr} from './util';

// This file defines the d3 SVG plot for the Parallel Coordinates panel.
// Note: The main entry point for the Parallel Coordinates panel is in PanelParallelCoord.tsx

const COLOR_SCALE_WIDTH = 30;
const AXIS_TITLE_APPROX_CHAR_WIDTH = 6;

type PCPlotSVGProps = {
  runs: RunWithRunsetInfo[];
  customRunColors?: RunColorConfig;
  customRunNames?: CustomRunNames;
  plotWidth: number;
  plotHeight: number;
  config: PCConfig;
  updateConfig: (newConfig: PCConfig) => void;
};

export const PCPlotSVG: React.FC<PCPlotSVGProps> = React.memo(props => {
  const plotDivRef = useRef<HTMLDivElement | null>(null);
  const columnCount = (props.config.columns ?? []).length;

  const svgParams = useRenderSVGParams(props);

  useEffect(() => {
    const plotDiv = plotDivRef.current;
    if (plotDiv != null && columnCount > 0) {
      renderSVG({...svgParams, node: plotDiv});
    }
  }, [columnCount, svgParams]);

  return <div className="fill" ref={plotDivRef} data-test="pc-plot-svg" />;
});
PCPlotSVG.displayName = 'PCPlotSVG';

function useRenderSVGParams({
  runs,
  customRunColors,
  customRunNames,
  plotWidth,
  plotHeight,
  config,
  updateConfig,
}: PCPlotSVGProps) {
  const {customGradient, gradientColor, legendFields, columns = []} = config;

  const rows = useMemo(
    () =>
      runs.map(run => {
        const row: {[key: string]: RunTypes.Value} = {name: run.name};
        for (const {accessor} of columns) {
          if (accessor != null) {
            row[accessor] = Run.getValueFromKeyString(run, accessor);
          }
        }
        return row;
      }),
    [columns, runs]
  );

  const {runSetRefs} = usePanelsSharedRefsContext();
  const runSets = ViewHooks.useParts(runSetRefs);

  const {getGroupKeysByRunsetId, isRunsetGrouped} = usePanelGroupingSettings();

  const groupSelectionsRefs = React.useMemo(
    () =>
      runSets
        .filter(({enabled}) => enabled)
        .map(({groupSelectionsRef}) => groupSelectionsRef),
    [runSets]
  );
  const groupSelections = ViewHooks.useParts(groupSelectionsRefs);

  const setGroupSelection = ViewHooks.useViewAction(
    runSets[0].groupSelectionsRef,
    GroupSelectionsActionsInternal.setGroupSelections
  );

  // The selected regions on the columns (created by brushCallback)
  const columnSelections = React.useMemo(() => {
    const result: SM.PanelSelections = {};
    for (const {accessor} of columns) {
      if (accessor == null) {
        continue;
      }
      const key = Run.keyFromString(accessor);
      if (!key) {
        continue;
      }
      result[accessor] = SM.getConstraintsForKey(groupSelections[0], key);
    }
    return result;
  }, [columns, groupSelections]);

  const convertSelectionToFilters = useConvertSelectionsToFilters(
    columnSelections,
    runSets.map(r => r.filtersRef)
  );

  const clearSelection = useCallback(
    (axis: string) =>
      setGroupSelection(SM.clearSelections([axis], groupSelections[0])),
    [groupSelections, setGroupSelection]
  );

  // D3 brush event, called when selecting a region on a column
  const brushCallback = useCallback(
    ({
      axis,
      selectionConstraint,
    }: {
      axis: string;
      selectionConstraint: SM.PanelSelectionConstraint;
    }) => {
      const {low, high, match} = selectionConstraint;
      const key = Run.keyFromString(axis);

      if (key == null) {
        return;
      }

      if (low == null && high == null && match == null) {
        clearSelection(axis);
        return;
      }

      const selectionWithoutKey = produce(groupSelections[0], draft => {
        draft.selections.bounds = draft.selections.bounds.filter(
          b => !Run.keysEqual(key, b.key)
        );
      });
      const selectedRuns = runs.filter(r => {
        const checkboxState = SM.getCheckedState(selectionWithoutKey, r, 1);
        return checkboxState === 'checked';
      });
      const emptySelection = !selectedRuns.some(r => {
        const val = Run.getValue(r, key);

        if (typeof val === 'number') {
          const valNotTooLow = low == null || val >= low;
          const valNotTooHigh = high == null || val <= high;
          const valInBounds = valNotTooLow && valNotTooHigh;
          return valInBounds;
        }

        if (typeof val === 'string') {
          return match?.includes(val) ?? false;
        }

        return false;
      });
      if (emptySelection) {
        // If no runs are selected, clear the selection
        toast('No runs selected');
        clearSelection(axis);
        return;
      }

      setGroupSelection(
        SM.setSelectionsToBounds(
          {[axis]: selectionConstraint} as SM.PanelSelections,
          groupSelections[0]
        )
      );
    },
    [clearSelection, groupSelections, runs, setGroupSelection]
  );

  const setHighlight = useSetHighlight();

  // mouseOver and mouseOut for plot lines
  const highlightCallback = useCallback(
    (run: RunWithRunsetInfo | null) => {
      setHighlight('run:name', run?.name);
    },
    [setHighlight]
  );

  // TODO: merge this with settings column reordering
  const columnDragendCallback = useCallback(
    (newColumnAccessorOrder: string[]) => {
      updateConfig({
        columns: newColumnAccessorOrder
          .map(accessor => _.find(columns, {accessor}))
          .filter(isNotNullOrUndefined),
      });
    },
    [columns, updateConfig]
  );

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const {entityName: entityNameFromUrl, projectName: projectNameFromUrl} =
    useProjectParams();
  const projectByRunsetId = React.useMemo(() => {
    return runSets.reduce(
      (acc, runSet) => {
        const entityName = runSet.project?.entityName ?? entityNameFromUrl;
        const projectName = runSet.project?.name ?? projectNameFromUrl;
        acc[runSet.id] = {entityName, projectName};
        return acc;
      },
      {} as Record<string, {entityName: string; projectName: string}>
    );
  }, [runSets, entityNameFromUrl, projectNameFromUrl]);

  // These are all the params passed to renderSVG()
  // node is added when the function is called in useEffect
  const renderSVGParams: Omit<SVGParams, 'node'> = React.useMemo(
    () => ({
      // node: element,
      boxWidth: plotWidth,
      boxHeight: plotHeight,
      rows,
      columns,
      columnSelections,
      groupSelections,
      runs,
      projectByRunsetId,
      customRunColors,
      customRunNames,
      customGradient: customGradient ?? [],
      gradientColor: gradientColor ?? true,
      legendFields: legendFields ?? ['run:displayName'],
      brushCallback,
      highlightCallback,
      dragendCallback: columnDragendCallback,
      clearSelection,
      convertSelectionToFilters,
      isRunsetGrouped,
      getGroupKeysByRunsetId,
      colorPaletteOption,
    }),
    [
      brushCallback,
      clearSelection,
      columnDragendCallback,
      columnSelections,
      columns,
      convertSelectionToFilters,
      customGradient,
      customRunColors,
      customRunNames,
      gradientColor,
      groupSelections,
      highlightCallback,
      legendFields,
      plotHeight,
      plotWidth,
      rows,
      runs,
      projectByRunsetId,
      isRunsetGrouped,
      getGroupKeysByRunsetId,
      colorPaletteOption,
    ]
  );
  return renderSVGParams;
}

// These are the params for renderSVG(), the function that draws the svg plot
type SVGParams = {
  node: HTMLDivElement;
  rows: Array<{[key: string]: RunTypes.Value}>;
  columns: PCColumn[];
  columnSelections: SM.PanelSelections;
  groupSelections: SM.GroupSelectionState[];
  runs: RunWithRunsetInfo[];
  projectByRunsetId: Record<string, {entityName: string; projectName: string}>;
  customRunColors?: RunColorConfig;
  customRunNames?: CustomRunNames;
  gradientColor: boolean;
  customGradient: GradientStop[];
  legendFields: string[];
  boxWidth: number;
  boxHeight: number;
  brushCallback: (params: {
    axis: string;
    selectionConstraint: SM.PanelSelectionConstraint;
  }) => void;
  highlightCallback: (run: RunWithRunsetInfo | null) => void;
  dragendCallback: (newColumnAccessorOrder: string[]) => void;
  clearSelection: (axis: string) => void;
  convertSelectionToFilters: (axis: string) => void;
  isRunsetGrouped: (runSetID: string) => boolean;
  getGroupKeysByRunsetId: (runSetID: string) => RunTypes.Key[];
  colorPaletteOption: ColorUtil.ColorPaletteOption;
};

type Extent = [number, number] | [null, null];

const MARGIN = {top: 30, right: 30, bottom: 10, left: 30};

// Renders the d3 graph
function renderSVG(params: SVGParams): void {
  const {
    node,
    rows,
    columns,
    columnSelections,
    groupSelections,
    runs,
    projectByRunsetId,
    customRunColors,
    customRunNames,
    brushCallback,
    // mouseOverCallback,
    // mouseOutCallback,
    dragendCallback,
    highlightCallback,
    gradientColor,
    legendFields,
    customGradient,
    boxWidth,
    boxHeight,
    clearSelection,
    convertSelectionToFilters,
    isRunsetGrouped,
    getGroupKeysByRunsetId,
  } = params;
  let isBrushing = false;

  const width = boxWidth - MARGIN.left - MARGIN.right;
  // The - 6 below corrects for an issue where the chart would grow
  // by 6 pixels on every render. I didn't root-cause, so this issue
  // will probably come back...
  const height = boxHeight - MARGIN.top - MARGIN.bottom - 6;

  const yScale = d3.scaleLinear().domain([0, 1]).range([height, 0]);

  const gradientColors =
    customGradient.length > 0
      ? gradientToRGBAValues(customGradient, 10)
      : PLASMA_GRADIENT;

  // add some colours for disabled values, and desaturated versions of the extrema
  // for positive and negative infinity.
  const allColors = _.clone(gradientColors);
  allColors.push([180, 180, 180, 1]);
  const grayIndex = allColors.length - 1;
  allColors.push([70.5, 84.5, 117.5, 1]);
  const negInfColorIdx = allColors.length - 1;
  allColors.push([172.5, 169, 117.5, 1]);
  const posInfColorIdx = allColors.length - 1;

  const runIndices = _.range(0, rows.length);

  const selectedRunIndices = runIndices.filter(runIdx => {
    const checkboxStates = groupSelections.map(groupSelection =>
      SM.getCheckedState(groupSelection, runs[runIdx], 1)
    );
    return checkboxStates.indexOf('checked') !== -1;
  });

  const accessorsWithDimensions = getAccessorsWithDimensions(
    rows,
    columns,
    yScale,
    gradientColors,
    grayIndex,
    posInfColorIdx,
    negInfColorIdx
  );
  if (accessorsWithDimensions.length === 0) {
    return;
  }

  const accessors = accessorsWithDimensions.map(({accessor}) => accessor);
  const dimensions = accessorsWithDimensions.map(({dimension}) => dimension);
  const lastAccessor = _.last(accessors)!;
  const lastDimension = _.last(dimensions)!;
  const dimensionsByAccessor: Map<string, Dimension> = new Map(
    accessorsWithDimensions.map(({accessor, dimension}) => [
      accessor,
      dimension,
    ])
  );

  // Render lines in order of their values for the last column,
  // starting from the top of the column to the bottom.
  // This makes it so that when the user hovers over multiple lines,
  // the line that ends up closer to the bottom of the last column takes precedence.
  const sortedRunIndices = _.sortBy(runIndices, runIdx =>
    getPlotVal(lastAccessor, runIdx)
  );
  const sortedSelectedRunIndices = _.sortBy(selectedRunIndices, runIdx =>
    getPlotVal(lastAccessor, runIdx)
  );
  const sortedRunIdxByRunIdx: Map<number, number> = new Map(
    sortedRunIndices.map((runIdx, i) => [runIdx, i])
  );

  const axisWidth = boxWidth / accessors.length;
  const axisTitleMaxLength = Math.round(
    axisWidth / AXIS_TITLE_APPROX_CHAR_WIDTH
  );

  const draggedColXPosByAcc: Map<string, number | undefined> = new Map();
  const extents: Extent[] = accessors.map(() => [null, null]);
  const gradientID =
    gradientColor && customGradient.length > 0
      ? customGradient.map(s => d3.rgb(s.color).r).join('')
      : 'svgGradient';

  const getColumnXPosition = d3
    .scaleBand()
    .range([0, width])
    .domain(accessors)
    .align(0.5)
    .paddingInner(0.9)
    .paddingOuter(0.1);

  const svg = d3
    .select(node)
    .html('')
    .append('svg')
    .attr('id', 'parallel-coordinates')
    .attr('class', 'parallel-coordinates-plot')
    .attr('width', width + MARGIN.left + MARGIN.right)
    .attr('height', height + MARGIN.top + MARGIN.bottom)
    .style('width', width + MARGIN.left + MARGIN.right + 'px')
    .style('height', height + MARGIN.top + MARGIN.bottom + 'px')
    .append('g')
    .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

  if (gradientColor) {
    const gradient = svg
      .append('defs')
      .append('linearGradient')
      .attr('id', gradientID)
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '100%')
      .attr('y2', '0%');

    if (customGradient.length > 0) {
      customGradient.forEach(stop => {
        gradient
          .append('stop')
          .attr('offset', `${stop.offset}%`)
          .attr('stop-color', stop.color);
      });
    } else {
      // legacy gradient
      gradientColors.forEach((rgba, i) => {
        gradient
          .append('stop')
          .attr('offset', `${(100 * i) / (gradientColors.length - 1)}%`)
          .attr('stop-color', rgbaStr(rgba));
      });
    }
  }

  // Add grey background lines for context.
  const background = svg
    .append('g')
    .attr('class', 'background')
    .selectAll('path')
    .data(sortedRunIndices)
    .enter()
    .append('path')
    .attr('stroke-width', 1)
    .attr('opacity', 0.2)
    .attr('d', path);

  const foreground = svg
    .append('g')
    .attr('class', 'foreground')
    .selectAll('path')
    .data(sortedRunIndices)
    .enter()
    .append('path')
    .attr('d', path)
    .attr('class', 'line')
    .attr('stroke-width', 1);

  const tooltip = d3
    .select(node)
    .append('div')
    .attr('class', 'hint')
    .style('z-index', '10')
    .style('position', 'absolute')
    .style('visibility', 'hidden');

  // Selection bounds menu (clear selection + convert selection to filters)
  let selectionBoundsMenuTimeoutID: number | null = null;
  const selectionBoundsMenu = d3
    .select(node)
    .append('div')
    .attr('class', 'selection-bounds-menu')
    .on('mouseleave', hideSelectionBoundsMenu)
    .on('mouseover', clearHideSelectionBoundsMenuTimeout);

  const colorScale = svg
    .append('rect')
    .attr('class', 'color-scale')
    .attr('fill', 'url(#' + gradientID + ')')
    .attr('width', String(COLOR_SCALE_WIDTH))
    .style('visibility', gradientColor ? 'visible' : 'hidden');

  setColorScale();
  addHover();

  // Add a group element for each dimension.
  const g = svg
    .selectAll('.dimension')
    .data(accessors)
    .enter()
    .append('g')
    .attr('class', 'dimension')
    .attr('transform', d => `translate(${getColumnXPosition(d)})`)
    .call(
      d3
        .drag()
        .subject(accessor => ({x: getColumnXPosition(accessor as string)}))
        .on('start', accessor => {
          draggedColXPosByAcc.set(
            accessor as string,
            getColumnXPosition(accessor as string)
          );
          background.attr('visibility', 'hidden');
        })
        .on('drag', accessor => {
          draggedColXPosByAcc.set(
            accessor as string,
            Math.min(width, Math.max(0, d3.event.x))
          );
          foreground.attr('d', path);
          setColorScale();
          accessors.sort(
            (a, b) => (getPosition(a) ?? 0) - (getPosition(b) ?? 0)
          );
          getColumnXPosition.domain(accessors);
          g.attr('transform', (d2: any) => `translate(${getPosition(d2)})`);
        })
        .on('end', function (accessor) {
          draggedColXPosByAcc.delete(accessor as string);
          transition(d3.select(this)).attr(
            'transform',
            `translate(${getColumnXPosition(accessor as string)})`
          );
          transition(foreground).attr('d', path);
          background
            .attr('d', path)
            .transition()
            .delay(500)
            .duration(0)
            .attr('visibility', null);
          addHover();
          dragendCallback(accessors);
        }) as any
    );

  // Add an axis and title.
  g.append('g')
    .attr('class', 'axis')
    .each(function (accessor) {
      const dimension = getDimension(accessor);
      const axis = d3.axisLeft(dimension.plotScale);
      d3.select(this).call(axis);
    })
    // text does not show up because previous line breaks somehow
    .append('text')
    .attr('class', 'axis-title')
    .style('text-anchor', 'middle')
    .attr('y', -15)
    .text((d, i) => {
      const {displayName, accessor} = columns[i];
      const title = displayName || Run.keyStringDisplayName(accessor ?? '');
      return truncateAxisTitle(title);
    });

  // Add and store a brush for each axis.
  // This is the grey rectangle that appears when you make a selection on the axis
  g.append('g')
    .attr('class', 'brush')
    .each(function (accessor, i) {
      const dimension = getDimension(accessor);
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const brushEl = this;
      const axisBrush = d3.brushY().extent([
        [-8, 0],
        [8, height],
      ]);
      dimension.brush = axisBrush;
      const brushSelect = columnSelections[accessor];
      d3.select(brushEl).call(axisBrush);

      if (brushSelect != null) {
        handleBrushSelect(brushSelect);
      }

      axisBrush
        .on('brush start', brushstart)
        .on('brush', brush)
        .on('end', brushend as any);
      d3.select(brushEl).call(axisBrush);

      return;

      function handleBrushSelect({
        low,
        high,
        match,
      }: SM.PanelSelectionConstraint): void {
        if (dimension == null) {
          return;
        }
        let topPx: number | undefined;
        let bottomPx: number | undefined;
        if (low != null && high != null) {
          // Numeric dimensions
          const inverted =
            columns.find(c => c.accessor === accessor)?.inverted ?? false;
          [topPx, bottomPx] = [low, high].map(dimension.plotScale);
          if (inverted) {
            [bottomPx, topPx] = [topPx, bottomPx];
          }
        } else if (match != null) {
          // String dimensions - snap to nearest value
          const matchVals = match.map(val =>
            // convert null to '<null>' (val should match lookup in StringDimension.domain)
            val == null ? NULL_STRING_ANGLE_BRACKETS : val
          );
          [topPx, bottomPx] = [
            matchVals[0],
            matchVals[matchVals.length - 1],
          ].map(dimension.plotScale);
          // Add some padding so the rectangle extends a bit above and below the range
          // (Especially important if only one value is selected)
          // This might be confusing if ticks are less than 10px apart on the axis
          // But if that's the case you probably have bigger problems
          if (topPx != null && bottomPx != null) {
            topPx = topPx + 10;
            bottomPx = bottomPx - 10;
          }
        }

        if (topPx != null && bottomPx != null) {
          attachSelectionBoundsMenu(topPx, bottomPx);
        }
      }

      function attachSelectionBoundsMenu(
        topPx: number,
        bottomPx: number
      ): void {
        const truncTopPx = Math.min(topPx, height);
        const truncBottomPx = Math.max(bottomPx, 0);
        extents[i] = [truncBottomPx, truncTopPx];
        axisBrush.move(d3.select(brushEl), [truncBottomPx, truncTopPx]);
        d3.select(brushEl)
          .on('mouseover', dim => {
            clearHideSelectionBoundsMenuTimeout();
            if (typeof dim === 'string') {
              renderAndShowSelectionBoundsMenu(dim, {
                top: Math.floor(truncBottomPx) + 30,
                left: Math.floor(getColumnXPosition(dim) ?? 0) + 38,
              });
            }
          })
          .on('mouseleave', hideSelectionBoundsMenu);
      }
    })
    .selectAll('rect')
    .attr('x', -8)
    .attr('width', 16);

  updateLinesForBrushes();
  return;

  function setColorScale(): void {
    // the 0.5 makes the left border coincide with the rightmost axis
    const colorScaleX = (getColumnXPosition(lastAccessor) ?? 0) + 0.5;
    // the 0.5 centers the border in its pixels, lining it up with the axes and getting rid of the anti-aliasing
    const colorScaleY =
      Math.min(lastDimension.colorStartY, lastDimension.colorEndY) + 0.5;
    const colorScaleHeight = Math.abs(
      lastDimension.colorStartY - lastDimension.colorEndY
    );
    colorScale
      .attr('x', colorScaleX)
      .attr('y', colorScaleY)
      .attr('height', colorScaleHeight);

    foreground.attr('stroke', runIdx => {
      if (!gradientColor) {
        return ColorUtil.runColor({
          run: runs[runIdx],
          customRunColors,
          colorPaletteOption: params.colorPaletteOption,
        });
      }

      // fix bug where colorIdx could be bigger than ALL_COLORS.length
      const colorIdx = lastDimension.colors[runIdx] ?? 0;
      const clampedColorIdx = _.clamp(colorIdx, 0, allColors.length - 1);
      return rgbaStr(allColors[clampedColorIdx] ?? [0, 0, 0, 1]);
    });
    foreground.attr('class', 'night-aware');
  }

  // Add wider transparent lines as hover targets (reusable function)
  function addHover(): void {
    // Remove any previously added lines
    svg.selectAll('g.hoverable').remove();
    // Add new lines based on latest data
    svg
      .append('g')
      .attr('class', 'hoverable')
      .attr('stroke-width', 8)
      .selectAll('path')
      .data(sortedSelectedRunIndices)
      .enter()
      .append('path')
      .attr('d', path)
      .on('mouseover', runIdx => {
        highlightRun(runIdx);
        // mouseOverCallback(runIdx);
      })
      .on('mouseout', () => {
        clearHighlight();
        // mouseOutCallback();
      })
      .on('mouseup', runIdx => {
        // Navigate to the run on cmd+click
        if (!d3.event?.metaKey) {
          return;
        }
        const runSetId = runs[runIdx].runsetInfo?.id;
        if (isRunsetGrouped(runSetId)) {
          return;
        }
        const {entityName, projectName} = projectByRunsetId[runSetId];
        window.open(
          urlPrefixed(
            runURL({
              entityName: entityName,
              projectName: projectName,
              name: runs[runIdx].name,
            })
          ),
          '_blank',
          'noopener,noreferrer'
        );
      });
  }

  function getPosition(accessor: string): number | undefined {
    return draggedColXPosByAcc.get(accessor) ?? getColumnXPosition(accessor);
  }

  function getPlotVal(accessor: string, runIdx: number): number {
    const dimension = getDimension(accessor);
    return dimension.plotVals[runIdx] as number;
  }

  // Returns the path for a given data point.
  function path(runIdx: number): string | null {
    const vals: Array<[number, number]> = accessors.map(accessor => [
      (getPosition(accessor) as number) + 0.5,
      getPlotVal(accessor, runIdx) + 0.5,
    ]);

    const l = d3.line().curve(d3.curveMonotoneX);
    return l(vals);
  }

  function brushstart(): void {
    isBrushing = true;
    // brushstart gets called on mousedown; reset the run selection
    // in case there was a previous brush that got destroyed
    brush();
  }

  function updateLinesForBrushes(): void {
    // makes the selected runs colorful and the non selected runs gray
    foreground.style('display', runIdx => {
      const runIsSelected = selectedRunIndices.includes(runIdx);
      return runIsSelected ? null : 'none';
    });
  }

  // Handles a brush event, toggling the display of foreground lines.
  function brush(): void {
    updateExtentForTarget(d3.event?.target);
    updateLinesForBrushes();
    return;

    function updateExtentForTarget(target: any): void {
      if (target == null) {
        return;
      }
      for (let i = 0; i < accessors.length; ++i) {
        const accessor = accessors[i];
        const dimension = getDimension(accessor);
        if (target === dimension.brush) {
          extents[i] = getExtent();
        }
      }
    }

    function getExtent(): Extent {
      const selection = d3.event?.selection;
      if (selection == null) {
        return [null, null];
      }
      const [start, end] = selection;
      if (start === end) {
        return [null, null];
      }
      return [start, end];
    }
  }

  function brushend(accessor: string): void {
    isBrushing = false;
    brushCallback({
      axis: accessor,
      selectionConstraint: getSelectionConstraint(),
    });
    return;

    function getSelectionConstraint(): SM.PanelSelectionConstraint {
      const selection = d3.event?.selection;
      if (selection == null) {
        return {};
      }

      const dimension = getDimension(accessor);
      const inverted =
        columns.find(c => c.accessor === accessor)?.inverted ?? false;
      return dimension.getSelectionConstraint(selection, inverted);
    }
  }

  function highlightRun(runIdx: number): void {
    if (isBrushing) {
      return;
    }

    // highlight the hovered line and dim the other lines
    svg
      .select('.foreground')
      .classed('hover', true)
      .select(`.line:nth-child(${getSortedVisibleRunIdx(runIdx) + 1}`)
      .classed('hover', true);

    const run = runs[runIdx];
    const [mouseX, mouseY] = d3.mouse(node);
    const mouseInLeftHalf = mouseX < width / 2;
    const mouseInTopHalf = mouseY < height / 2;

    tooltip.style('visibility', 'visible');
    if (mouseInLeftHalf) {
      const tooltipLeft = mouseX + 12;
      tooltip.style('left', `${tooltipLeft}px`).style('right', '');
    } else {
      const tooltipRight = width - mouseX - 4;
      tooltip.style('right', `${tooltipRight}px`).style('left', '');
    }
    if (mouseInTopHalf) {
      const tooltipTop = mouseY + 16;
      tooltip.style('top', `${tooltipTop}px`).style('bottom', '');
    } else {
      const tooltipBottom = height - mouseY + 60;
      tooltip.style('bottom', `${tooltipBottom}px`).style('top', '');
    }

    const allKeys = _.uniq([...legendFields, ...accessors]);
    const runSetId = run.runsetInfo?.id;
    const groupKeys =
      runSetId && isRunsetGrouped(runSetId)
        ? getGroupKeysByRunsetId(runSetId)
        : [];
    tooltip.html(legendHTMLFromRun(run, allKeys, groupKeys, customRunNames));

    highlightCallback(run);
  }

  function clearHighlight(): void {
    if (isBrushing) {
      return;
    }
    svg.selectAll('.hover').classed('hover', false);
    tooltip.style('visibility', 'hidden');
    highlightCallback(null);
  }

  function renderAndShowSelectionBoundsMenu(
    axis: string,
    coords: {top: number; left: number}
  ): void {
    ReactDOM.render(
      <SelectionBoundsMenu
        clearSelections={() => clearSelection(axis)}
        convertSelectionsToFilters={() => convertSelectionToFilters(axis)}
      />,
      selectionBoundsMenu.node()
    );
    selectionBoundsMenu.style('top', `${coords.top}px`);
    selectionBoundsMenu.style('left', `${coords.left}px`);
    selectionBoundsMenu.style('visibility', 'visible');
  }

  function hideSelectionBoundsMenu(): void {
    selectionBoundsMenuTimeoutID = window.setTimeout(() => {
      selectionBoundsMenu.style('visibility', 'hidden');
    }, 1000);
  }

  function clearHideSelectionBoundsMenuTimeout(): void {
    if (selectionBoundsMenuTimeoutID != null) {
      clearTimeout(selectionBoundsMenuTimeoutID);
    }
  }

  function getSortedVisibleRunIdx(runIdx: number): number {
    const sortedRunIdx = sortedRunIdxByRunIdx.get(runIdx);
    if (sortedRunIdx == null) {
      throw new Error(`invalid run index: ${runIdx}`);
    }
    return sortedRunIdx;
  }

  function getDimension(accessor: string): Dimension {
    const dimension = dimensionsByAccessor.get(accessor);
    if (dimension == null) {
      throw new Error(`invalid accessor: ${accessor}`);
    }
    return dimension;
  }

  function transition(el: any): any {
    return el.transition().duration(500);
  }

  function truncateAxisTitle(str: string): string {
    if (str.length <= axisTitleMaxLength) {
      return str;
    }
    return str.slice(0, Math.max(axisTitleMaxLength - 3, 1)) + '...';
  }
}
